import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Diagnose the Problems',
    description: 'Systematically analyze the web server to identify all configuration and operational issues that are preventing it from working correctly.',
    requirements: [
      {
        text: 'Determine the current state of the nginx service'
      },
      {
        text: 'Identify which ports the web server is currently using'
      },
      {
        text: 'Check system logs to understand service failures'
      },
      {
        text: 'Test connectivity to identify what is and isn\'t working'
      },
      {
        text: 'Document all issues you discover for systematic resolution'
      }
    ],
    verification: [
      'You have identified the nginx service status',
      'You know which port nginx is incorrectly listening on',
      'You have reviewed relevant error logs',
      'You understand why HTTPS connections are failing',
      'You have a clear list of problems to fix'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Fix Port Configuration',
    description: 'Correct the web server configuration so it listens on standard HTTP and HTTPS ports instead of non-standard ports.',
    requirements: [
      {
        text: 'Locate and examine the nginx site configuration'
      },
      {
        text: 'Correct the port bindings to use standard web ports:',
        subitems: ['Port 80 for HTTP traffic', 'Port 443 for HTTPS traffic']
      },
      {
        text: 'Ensure both IPv4 and IPv6 configurations are correct if present'
      },
      {
        text: 'Validate the configuration syntax before applying changes'
      },
      {
        text: 'Apply the configuration and ensure the service restarts successfully'
      }
    ],
    verification: [
      'Nginx configuration uses port 80 for HTTP',
      'Nginx configuration includes port 443 for HTTPS',
      'Configuration syntax validation passes without errors',
      'Nginx service restarts successfully',
      'Server responds to HTTP requests on port 80'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Configure SSL Certificates',
    description: 'Set up valid SSL certificates to enable secure HTTPS connections for the web server.',
    requirements: [
      {
        text: 'Check the current SSL configuration and certificate status'
      },
      {
        text: 'Create the necessary directory structure for SSL certificates'
      },
      {
        text: 'Generate a self-signed SSL certificate valid for at least 365 days'
      },
      {
        text: 'Configure proper permissions on certificate files:',
        subitems: ['Private key should be readable only by root', 'Certificate should be world-readable']
      },
      {
        text: 'Update nginx configuration to use the SSL certificates'
      },
      {
        text: 'Enable HTTPS server block with proper SSL directives'
      }
    ],
    verification: [
      'SSL certificate directory exists with correct permissions',
      'Valid SSL certificate and private key are present',
      'Certificate is not expired (valid for at least 1 day)',
      'Nginx configuration includes SSL server block',
      'HTTPS connections work on port 443'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Fix Content Permissions',
    description: 'Ensure web content files have correct ownership and permissions for the web server to serve them properly.',
    requirements: [
      {
        text: 'Verify the document root directory exists and is correctly configured'
      },
      {
        text: 'Create an appropriate index.html file that confirms the rescue is complete'
      },
      {
        text: 'Set correct ownership for all web content:',
        subitems: ['Files and directories should be owned by the web server user', 'The web server user is typically www-data on Ubuntu']
      },
      {
        text: 'Configure appropriate permissions:',
        subitems: ['Directories should be readable and executable', 'Files should be readable by the web server']
      },
      {
        text: 'Verify the web server can access and serve the content'
      }
    ],
    verification: [
      'Document root directory exists at /var/www/html',
      'Index.html file exists with rescue completion content',
      'All web files are owned by www-data user and group',
      'Directory permissions allow web server access (755)',
      'File permissions allow web server to read content (644)',
      'Web server successfully serves the index page'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Configure Firewall',
    description: 'Ensure firewall rules allow incoming HTTP and HTTPS traffic to reach the web server.',
    requirements: [
      {
        text: 'Check the current firewall status and rules'
      },
      {
        text: 'Configure firewall to allow standard web traffic:',
        subitems: ['HTTP traffic on port 80', 'HTTPS traffic on port 443']
      },
      {
        text: 'Use appropriate firewall profiles if available (such as "Nginx Full")'
      },
      {
        text: 'Ensure firewall is active and rules are persistent'
      },
      {
        text: 'Verify connectivity through the firewall from multiple methods'
      }
    ],
    verification: [
      'Firewall is active and enabled',
      'Firewall rules allow port 80/tcp',
      'Firewall rules allow port 443/tcp',
      'HTTP connections successfully reach the web server',
      'HTTPS connections successfully reach the web server',
      'Firewall configuration will persist after reboot'
    ]
  }
];

const BrokenWebServerRescueInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrokenWebServerRescueInstructions;
